'use client';

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { PublicDesign } from '@/app/lib/community/shared';
import { SKIN_SIZE, VIEW_H, VIEW_W, parseSkinState, renderSkinView, skinDataUrl, type SkinModel } from '@/app/lib/community/skinLayout';

/**
 * A community skin, drawn flat and pixel-crisp from its UV layout: front view on gallery cards; front, back
 * and the skin file itself on the design page — so a moderator sees every pixel that is published (the
 * canonical form clears everything outside the UV layout, so the file view shows nothing the model can't).
 *
 * 2D canvas, not the library's WebGL `SkinPreview3D`: a listing page has 24 cards, and the flat view shows
 * the overlay layer exactly rather than at an angle.
 */

/** 'review' = every face in one compact row, for the admin queue — approving from a front-only thumbnail
 *  let anything on the sides, top or back through. */
type Size = 'grid' | 'list' | 'modal' | 'review';

interface Decoded { model: SkinModel; rgba: Uint8ClampedArray }

/** Decode the design's PNG once. A failure is final — no retry, so onerror cannot loop. */
function useSkinPixels(state: string): Decoded | 'error' | null {
  const [result, setResult] = useState<{ state: string; value: Decoded | 'error' } | null>(null);
  useEffect(() => {
    const parsed = parseSkinState(state);
    if (!parsed) {
      queueMicrotask(() => setResult({ state, value: 'error' }));
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      if (img.naturalWidth !== SKIN_SIZE || img.naturalHeight !== SKIN_SIZE) { setResult({ state, value: 'error' }); return; }
      const canvas = document.createElement('canvas');
      canvas.width = SKIN_SIZE;
      canvas.height = SKIN_SIZE;
      const ctx = canvas.getContext('2d');
      if (!ctx) { setResult({ state, value: 'error' }); return; }
      ctx.drawImage(img, 0, 0);
      setResult({ state, value: { model: parsed.model, rgba: ctx.getImageData(0, 0, SKIN_SIZE, SKIN_SIZE).data } });
    };
    img.onerror = () => { if (!cancelled) setResult({ state, value: 'error' }); };
    img.src = skinDataUrl(parsed.base64);
    return () => { cancelled = true; img.onload = null; img.onerror = null; };
  }, [state]);
  return result && result.state === state ? result.value : null;
}

/** RGBA pixels drawn at an integer scale with no smoothing, then sized by CSS. */
function PixelCanvas({ rgba, width, height, scale, className, style, label }: {
  rgba: Uint8ClampedArray; width: number; height: number; scale: number;
  className?: string; style?: CSSProperties; label: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const out = ctx.createImageData(width * scale, height * scale);
    for (let y = 0; y < height * scale; y++) {
      for (let x = 0; x < width * scale; x++) {
        const s = (Math.floor(y / scale) * width + Math.floor(x / scale)) * 4;
        const d = (y * width * scale + x) * 4;
        out.data[d] = rgba[s]; out.data[d + 1] = rgba[s + 1]; out.data[d + 2] = rgba[s + 2]; out.data[d + 3] = rgba[s + 3];
      }
    }
    ctx.putImageData(out, 0, 0);
  }, [rgba, width, height, scale]);
  return (
    <canvas
      ref={ref}
      width={width * scale}
      height={height * scale}
      role="img"
      aria-label={label}
      className={className}
      style={{ imageRendering: 'pixelated', ...style }}
    />
  );
}

const FRAME = 'w-full h-full flex items-center justify-center bg-[radial-gradient(120%_140%_at_50%_0%,#1b211d,#0f1311)]';
// Transparent pixels of the skin file shown on a checkerboard, as image editors do.
const CHECKER = { backgroundColor: '#27272a', backgroundImage: 'linear-gradient(45deg,#3f3f46 25%,transparent 25%,transparent 75%,#3f3f46 75%),linear-gradient(45deg,#3f3f46 25%,transparent 25%,transparent 75%,#3f3f46 75%)', backgroundSize: '12px 12px', backgroundPosition: '0 0,6px 6px' };

export default function SkinDesignPreview({ design, size }: { design: PublicDesign; size: Size }) {
  const pixels = useSkinPixels(design.state);
  const views = useMemo(() => (pixels && pixels !== 'error'
    ? { front: renderSkinView(pixels.rgba, pixels.model, 'front'), back: renderSkinView(pixels.rgba, pixels.model, 'back') }
    : null), [pixels]);

  if (pixels === 'error') {
    return <div className={`${FRAME} text-xs text-zinc-500`}>Preview unavailable</div>;
  }
  if (!pixels || !views) return <div className={FRAME} aria-busy="true" />;

  const { front, back } = views;
  const modelName = pixels.model === 'slim' ? 'slim (Alex)' : 'classic (Steve)';

  if (size === 'review') {
    return (
      <div className="flex flex-wrap items-end gap-3">
        {([['Front', front], ['Back', back]] as const).map(([name, view]) => (
          <figure key={name} className="flex flex-col items-center gap-1">
            <div className="h-32 w-16 rounded-md bg-zinc-950 flex items-center justify-center">
              <PixelCanvas rgba={view} width={VIEW_W} height={VIEW_H} scale={8} label={`${name} of the skin “${design.title}”`}
                style={{ height: '100%', width: 'auto', aspectRatio: '1 / 2' }} />
            </div>
            <figcaption className="text-[11px] text-zinc-500">{name}</figcaption>
          </figure>
        ))}
        <figure className="flex flex-col items-center gap-1">
          <div className="h-32 w-32 rounded-md overflow-hidden" style={CHECKER}>
            <PixelCanvas rgba={pixels.rgba} width={SKIN_SIZE} height={SKIN_SIZE} scale={4} label={`The skin file of “${design.title}”`}
              className="block w-full h-full" />
          </div>
          <figcaption className="text-[11px] text-zinc-500">Skin file · {modelName}</figcaption>
        </figure>
      </div>
    );
  }

  if (size !== 'modal') {
    return (
      <div className={FRAME}>
        <PixelCanvas
          rgba={front} width={VIEW_W} height={VIEW_H} scale={8} label={`Front of the skin “${design.title}”`}
          style={{ height: size === 'list' ? '88%' : '80%', width: 'auto', aspectRatio: '1 / 2' }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {([['Front', front], ['Back', back]] as const).map(([name, view]) => (
          <figure key={name} className="flex flex-col items-center gap-2">
            <div className="w-full aspect-[3/4] rounded-lg bg-zinc-900/60 flex items-center justify-center">
              <PixelCanvas
                rgba={view} width={VIEW_W} height={VIEW_H} scale={8} label={`${name} of the skin “${design.title}”`}
                style={{ height: '88%', width: 'auto', aspectRatio: '1 / 2' }}
              />
            </div>
            <figcaption className="text-xs text-zinc-500">{name}</figcaption>
          </figure>
        ))}
      </div>
      <figure className="flex flex-col items-center gap-2">
        <div className="w-full max-w-[16rem] rounded-lg overflow-hidden" style={CHECKER}>
          <PixelCanvas
            rgba={pixels.rgba} width={SKIN_SIZE} height={SKIN_SIZE} scale={4} label={`The skin file of “${design.title}”`}
            className="block w-full h-auto"
          />
        </div>
        <figcaption className="text-xs text-zinc-500">Skin file · {modelName} arms</figcaption>
      </figure>
    </div>
  );
}
